import { AdminResolver } from '@admin/admin.resolver';
import { AccountResolver } from '@agent/features/account/account.resolver';
import { CustomerResolver } from '@agent/features/customer/customer.resolver';
import { PartnerResolver } from '@agent/features/partner/partner.resolver';
import { AssignmentResolver } from '@agent/features/assignment/assignment.resolver';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  type ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { environment } from '@environments/environment';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as sentry from '@sentry/angular';
import { SentryHttpErrorHandler } from '@infra/error-handling/sentry-error-handler';
import { httpInterceptorProviders } from '@infra/http-interceptors';
import { CompanyCurrencyPipe } from '@common/pipes/company-currency.pipe';
import { CompanyNumberPipe } from '@common/pipes/company-number.pipe';
import { AuthTokenProviderService } from '@infra/auth/auth-token-provider.service';
import { SecurityService } from '@infra/auth/security.service';
import { CompanyResolver } from '@infra/resolvers/company.resolver';
import { appRoutes } from '@system/app.routes';
import { ReedgePreset } from '@infra/theme/reedge-preset';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { type NgHttpCachingConfig, NgHttpCachingModule, NgHttpCachingStrategy } from 'ng-http-caching';
import { pendingRequestsInterceptor$ } from 'ng-http-loader';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { ToastrModule } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';

// Not configured through provideTranslateService: that loads the language in the TranslateService constructor, and
// the request goes through our HTTP interceptors, which (via AlertService) inject TranslateService again (NG0200).
function initializeTranslations() {
  const translateService = inject(TranslateService);
  translateService.setFallbackLang('en');
  return firstValueFrom(translateService.use('en'));
}

function jwtOptionsFactory(tokenProviderService: AuthTokenProviderService) {
  return {
    tokenGetter: () => {
      return tokenProviderService.get();
    },
    allowedDomains: environment.jwt.allowedDomains,
    disallowedRoutes: environment.jwt.disallowedRoutes,
  };
}

const ngHttpCachingConfig: NgHttpCachingConfig = {
  cacheStrategy: NgHttpCachingStrategy.DISALLOW_ALL,
  allowedMethod: ['GET', 'POST', 'PUT'],
  lifetime: 1000 * 600,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$])),
    importProvidersFrom(
      BrowserModule,
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [AuthTokenProviderService],
        },
      }),
      ToastrModule.forRoot({
        // https://github.com/scttcper/ngx-toastr
        timeOut: 4000,
        positionClass: 'toast-top-right',
        preventDuplicates: false,
        enableHtml: true,
        closeButton: true,
      }),
      NgxGoogleAnalyticsModule.forRoot(environment.googleAnalytics.trackingId),
      NgxGoogleAnalyticsRouterModule,
      NgHttpCachingModule.forRoot(ngHttpCachingConfig),
      NgxDaterangepickerMd.forRoot()
    ),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: `${environment.api}/resource/ui/`, suffix: '.json' }),
    }),
    provideAppInitializer(initializeTranslations),
    SecurityService,
    httpInterceptorProviders,
    NgbActiveModal,
    providePrimeNG({
      theme: {
        preset: ReedgePreset,
        options: {
          darkModeSelector: false,
          // PrimeNG injects its styles at runtime. Layering them lets our (unlayered) global styles override PrimeNG,
          // while Bootstrap's element-level reboot (layered in main.scss) stays below PrimeNG.
          cssLayer: { name: 'primeng', order: 'reboot, primeng' },
        },
      },
      // Above the ng-bootstrap modals (1050).
      zIndex: {
        modal: 1055,
        overlay: 1055,
        tooltip: 1055,
        menu: 1055,
      },
      overlayOptions: {
        motionOptions: { disabled: true },
      },
    }),
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    DatePipe,
    CurrencyPipe,
    DecimalPipe,
    CompanyCurrencyPipe,
    CompanyNumberPipe,
    TranslatePipe,
    CompanyResolver,
    AdminResolver,
    AssignmentResolver,
    CustomerResolver,
    PartnerResolver,
    AccountResolver,
    {
      provide: ErrorHandler,
      useValue: new SentryHttpErrorHandler(),
    },
    {
      provide: sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Instantiates the trace service at startup (generated by Sentry Wizard).
      inject(sentry.TraceService);
    }),
  ],
};
